/**
 * One place where "this target was activated" turns into "this happens".
 *
 * Why a registry rather than an `onClick` on each button: §5.1 promises that a new input mode is a
 * new adapter and **zero UI changes**. Dwell and scanning (Phase 3) never produce a click — they
 * produce an `activate` intent from a timer. If surfaces listened for clicks, every one of them
 * would have to be reworked to support the input modes this person is most likely to need. So
 * surfaces register what a target *means*, and whatever drives input calls `activate`.
 *
 * Hardware switches matter here too: a Bluetooth switch presents as a keyboard sending Space or
 * Enter (§2.5), which arrives as a DOM click on the focused button. That path stays supported by
 * routing it through the same function.
 *
 * **Coalescing.** A single physical tap can arrive twice — once from the pointer adapter and once as
 * the synthetic click the browser generates afterwards. Suppressing the synthetic click is not
 * reliable across engines, so instead the same target activating twice within `COALESCE_MS` is
 * treated as one action.
 *
 * That window is 25 ms, and deliberately tiny: the two arrivals of one tap are milliseconds apart,
 * while a person typing a double letter is far slower. Making this window generous would silently
 * eat the second L of "HALLO". Tremor debounce is a separate, longer window and remains the
 * adapter's job, where it can be set per target class.
 */

import { noteExplicitGesture } from './activeAdapter';

/**
 * The controls whose *activation* is itself a user gesture (`CON-03`, C64, ADR-046).
 *
 * The override belongs here rather than in the components because this is where all three modes converge:
 * `App.tsx` turns every adapter's `activate` intent into a call to `activate()`, and `ActivateButton` routes
 * its own click through the same function. Left in a component's `onClick` — where C64 put it — it fired for a
 * finger and for nothing else, so the way out of a speech failure was refused in the two modes that need it.
 *
 * Checked by id and never by mode. `isTimed` says *activations from this adapter are timer-driven*; whether a
 * switch press should count as a gesture in general is `INP-15`'s open question and this must not answer it.
 */
export const TAP_TO_SPEAK_ID = 'action:tapToSpeak';
export const AUDIO_REPAIR_ID = 'action:audioRepair';

const EXPLICIT_GESTURE_IDS: ReadonlySet<string> = new Set([TAP_TO_SPEAK_ID, AUDIO_REPAIR_ID]);

type Handler = () => void;

const handlers = new Map<string, Handler>();

const COALESCE_MS = 25;
let lastId: string | null = null;
let lastAtMs = Number.NEGATIVE_INFINITY;

/** Targets whose activations are being swallowed, and until when. `SUR-26`. */
const suppressedUntil = new Map<string, number>();

const now = (): number => (typeof performance === 'undefined' ? 0 : performance.now());

/**
 * Swallow every activation of one target for a while (`SUR-26`, ADR-043).
 *
 * `DirectTouch` activates on **release within target**, whatever the duration, so a hold that has already
 * fired would be followed by an ordinary activation from the release that ended it — clearing the sentence
 * and then navigating away from it.
 *
 * This belongs here because this module is already the one place where "one physical action must not become
 * two activations" is decided: `COALESCE_MS` is the same question with a different window. The two
 * alternatives are worse in ways that are not obvious:
 *
 *  - **`stopPropagation` on the button.** The adapter listens on `document.documentElement`, so swallowing
 *    the event leaves its `pressTargetId` and `dataset.pressed` set — the bar stays inverted and the *next*
 *    release anywhere resolves as a miss, emitting `ActivationMissed`. That event is calibration's
 *    field-loss evidence (§5.5), and corrupting the data that substitutes for a diagnosis is not a fix.
 *  - **`setPointerCapture` on a wrapper.** Capture retargets later events to the capturing node, so
 *    `closest('[data-activate-id]')` finds nothing and the ordinary tap stops working.
 *
 * It is a **window rather than a one-shot** because both arrivals of one tap have to be caught, and the
 * synthetic click can land outside the 25 ms coalesce window.
 */
export function suppressActivations(id: string, ms: number): void {
  suppressedUntil.set(id, now() + ms);
}

export function registerActivation(id: string, handler: Handler): () => void {
  handlers.set(id, handler);
  return () => {
    // Only remove if it is still ours: a re-render may have replaced the handler already.
    if (handlers.get(id) === handler) handlers.delete(id);
  };
}

export function activate(id: string): void {
  const at = now();

  const until = suppressedUntil.get(id);
  if (until !== undefined) {
    if (at < until) return;
    // Expired. Dropped rather than left to accumulate — this map is keyed by target id and would
    // otherwise grow one stale entry per hold for the lifetime of the page.
    suppressedUntil.delete(id);
  }

  if (id === lastId && at - lastAtMs < COALESCE_MS) return;
  lastId = id;
  lastAtMs = at;

  const handler = handlers.get(id);
  if (!handler) {
    // A target that looks interactive and does nothing is worse than one that is not there, so this
    // is loud rather than silent.
    console.error('[input] no handler registered for', id);
    return;
  }

  // Before the handler, because the handler is what calls `speak()` and `inGesture()` is read inside it.
  if (EXPLICIT_GESTURE_IDS.has(id)) noteExplicitGesture();

  try {
    handler();
  } catch (err) {
    console.error('[input] handler failed for', id, err);
  }
}

export function hasActivation(id: string): boolean {
  return handlers.has(id);
}

/**
 * Register several targets at once, for elements that cannot be an `ActivateButton` — the body
 * map's SVG regions, mainly. Returns a single teardown.
 */
export function registerActivations(entries: ReadonlyArray<[string, Handler]>): () => void {
  const teardowns = entries.map(([id, handler]) => registerActivation(id, handler));
  return () => {
    for (const teardown of teardowns) teardown();
  };
}

/** Test seam. */
export function resetActivations(): void {
  handlers.clear();
  lastId = null;
  lastAtMs = Number.NEGATIVE_INFINITY;
  suppressedUntil.clear();
}
